import { OrganizationMarketplace, OrganizationMarketplaceServicesInfo } from '@spike/marketplace-model';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { useAuth } from './useAuth';
import { isSettingEnabled } from 'utils/SettingsUtils';

export const useOrganizationMarketplaces = (): {
    getOrganizationMarketplace: (marketplaceId: number) => OrganizationMarketplace | null;
    getOrganizationServicesInfo: (marketplaceId: number) => OrganizationMarketplaceServicesInfo | null;
    organizationMarketplaces: Array<OrganizationMarketplace>;
    organizationServicesInfo: Array<OrganizationMarketplaceServicesInfo>;
    isUserLoggedInMarketplace: (marketplaceId: number) => boolean;
    hasBoarding: (marketplaceId: number) => boolean;
    getOrganizationServiceCategories: () => Array<string>;
} => {
    const auth = useAuth();

    const organizationMarketplaces = useSelector<RootState, Array<OrganizationMarketplace>>(
        state => state.marketplace.organizationMarketplaces
    );

    const organizationServicesInfo = useSelector<RootState, Array<OrganizationMarketplaceServicesInfo>>(
        state => state.marketplace.organizationServicesInfo
    );

    const getOrganizationMarketplace = (marketplaceId: number) =>
        organizationMarketplaces.find(marketplace => marketplace.id === marketplaceId) ?? null;

    const getOrganizationServicesInfo = (marketplaceId: number) =>
        organizationServicesInfo.find(info => info.marketplace.id === marketplaceId) ?? null;

    const isUserLoggedInMarketplace = (marketplaceId: number) => marketplaceId === auth.marketplaceId;

    const hasBoarding = (marketplaceId: number) => {
        const marketplace = getOrganizationMarketplace(marketplaceId);
        return isSettingEnabled(marketplace?.settings, 'boarding_enabled');
    };

    const getOrganizationServiceCategories = () =>
        Array.from(
            organizationMarketplaces.reduce((set, marketplace) => {
                if (isSettingEnabled(marketplace.settings, 'grooming_enabled')) {
                    set.add('grooming');
                }
                if (isSettingEnabled(marketplace.settings, 'daycare_enabled')) {
                    set.add('daycare');
                }
                if (isSettingEnabled(marketplace.settings, 'boarding_enabled')) {
                    set.add('boarding');
                }
                if (isSettingEnabled(marketplace.settings, 'transportation_enabled')) {
                    set.add('transportation');
                }
                if (isSettingEnabled(marketplace.settings, 'evaluation_enabled')) {
                    set.add('evaluation');
                }
                if (isSettingEnabled(marketplace.settings, 'training_enabled')) {
                    set.add('training');
                }
                if (isSettingEnabled(marketplace.settings, 'walking_enabled')) {
                    set.add('walking');
                }
                if (marketplace.evaluationEnabled) {
                    set.add('evaluation');
                }

                return set;
            }, new Set<string>())
        ).sort((s1, s2) => s1.localeCompare(s2));

    return {
        getOrganizationMarketplace,
        getOrganizationServicesInfo,
        organizationMarketplaces: [...organizationMarketplaces],
        organizationServicesInfo: [...organizationServicesInfo],
        isUserLoggedInMarketplace,
        hasBoarding,
        getOrganizationServiceCategories
    };
};

export default useOrganizationMarketplaces;
